(() => {
  const sharedRootSelector = '#cart-drawer, [data-aoctant-button-wave-root]';
  const sharedTargetSelector = '[data-aoctant-button-wave]';

  const initialize = (root) => {
    if (root.dataset.aoctantWaveRootBound === 'true') return;
    root.dataset.aoctantWaveRootBound = 'true';

    const targetSelector = root.matches('#cart-drawer')
      ? [
          '.cart__checkout-button',
          '.aoctant-cart-view-button',
          '.aoctant-native-cart-accessories__action button.quick-add__button',
          '.aoctant-cart-complementary__action button.quick-add__button',
          '.theme-drawer__close-button',
          sharedTargetSelector,
        ].join(',')
      : sharedTargetSelector;
    const primarySelector = [
      '.cart__checkout-button',
      '.aoctant-native-cart-accessories__action button.quick-add__button',
      '.aoctant-cart-complementary__action button.quick-add__button',
      '[data-aoctant-button-wave="primary"]',
    ].join(',');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controls = new Set();
  const bindings = new WeakMap();
  const excludedForegroundSelector = [
    '.aoctant-cart-checkout__lock', '.add-to-cart__added', '[aria-live]', '[role="status"]', '[data-loading]', '[data-status]',
    '.loading', '.is-loading', '.loader', '.spinner', '.success', '.is-success', '.error', '.is-error',
  ].join(',');
  const excludedClassPattern = /(added|success|checkmark|loading|loader|spinner|status|error)/i;

  const cssVar = (control, name, fallback) =>
    getComputedStyle(control).getPropertyValue(name).trim() || fallback;

  const duration = (control) => {
    const value = cssVar(control, '--aoctant-button-wave-duration', '1000ms');
    return value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
  };

  /** Returns the native Horizon state that currently owns presentation, if any. */
  const getNativeState = (control) => {
    if (control.disabled || control.getAttribute('aria-disabled') === 'true') return 'disabled';
    if (control.getAttribute('aria-busy') === 'true') return 'processing';
    if (control.matches('.loading, .is-loading, .processing, .is-processing')) return 'processing';
    if (control.dataset.added === 'true' || control.matches('.success, .is-success')) return 'success';
    if (control.matches('.error, .is-error')) return 'error';
    return null;
  };

  const isExcludedForeground = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.matches(excludedForegroundSelector) || node.querySelector(excludedForegroundSelector)) return true;
    return Array.from(node.classList).some((className) => excludedClassPattern.test(className));
  };

  const createForegroundLayers = (control) => {
    const sourceNodes = [];

    Array.from(control.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && node.matches('[data-aoctant-wave-decoration]')) return;
      if (isExcludedForeground(node)) return;

      if (node.nodeType === Node.TEXT_NODE) {
        if (!node.textContent.trim()) return;
        const source = document.createElement('span');
        source.className = 'aoctant-button-wave__source aoctant-button-wave__source-label';
        node.replaceWith(source);
        source.append(node);
        sourceNodes.push(source);
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        node.classList.add('aoctant-button-wave__source');
        sourceNodes.push(node);
      }
    });

    const createLayer = (modifier) => {
      const layer = document.createElement('span');
      layer.className = `aoctant-button-wave__foreground aoctant-button-wave__foreground--${modifier}`;
      layer.dataset.aoctantWaveDecoration = '';
      layer.setAttribute('aria-hidden', 'true');
      sourceNodes.forEach((node) => layer.append(node.cloneNode(true)));
      return layer;
    };

    return {
      base: createLayer('base'),
      sweep: createLayer('sweep'),
    };
  };

  const createLoadingFeedback = () => {
    const loader = document.createElement('span');
    loader.className = 'aoctant-button-wave__feedback aoctant-button-wave__loader';
    loader.dataset.aoctantWaveDecoration = '';
    loader.setAttribute('aria-hidden', 'true');
    loader.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));
    return loader;
  };

  const hasNativeProcessingFeedback = (control) =>
    Array.from(control.querySelectorAll('.spinner, .loader, [data-loading], [role="status"], [aria-live]')).some(
      (element) => !element.closest('[data-aoctant-wave-decoration]')
    );

  const bind = (control) => {
    const previous = bindings.get(control);
    if (previous?.intact()) return;
    previous?.dispose();
    control.querySelectorAll(':scope > [data-aoctant-wave-decoration]').forEach((node) => node.remove());

    control.classList.add('aoctant-button-wave');
    control.classList.add(
      control.matches(primarySelector)
        ? 'aoctant-button-wave--primary'
        : 'aoctant-button-wave--reversed'
    );
    const isCircle = control.matches(
      '.theme-drawer__close-button, [data-aoctant-button-wave="circle"]'
    );
    if (isCircle) control.classList.add('aoctant-button-wave--circle');
    control.dataset.aoctantWaveBound = 'true';
    control.dataset.aoctantWaveSurface = 'rest';

    const wave = document.createElement('span');
    wave.className = 'aoctant-button-wave__wave';
    wave.dataset.aoctantWaveDecoration = '';
    wave.setAttribute('aria-hidden', 'true');

    const foreground = createForegroundLayers(control);
    const loadingFeedback = createLoadingFeedback();
    const checkoutLock = control.querySelector(':scope > .aoctant-cart-checkout__lock');

    control.append(wave, foreground.base, foreground.sweep, loadingFeedback);

    const state = {
      committed: 'rest', desired: 'rest', running: false, generation: 0, nativeOwned: false,
      activeTarget: 'rest',
      waveAnimation: null, foregroundAnimation: null, lockAnimation: null,
    };

    const cancelAnimations = () => {
      state.waveAnimation?.cancel();
      state.foregroundAnimation?.cancel();
      state.lockAnimation?.cancel();
      state.waveAnimation = null;
      state.foregroundAnimation = null;
      state.lockAnimation = null;
    };

    const commit = (surface) => {
      state.committed = surface;
      control.dataset.aoctantWaveSurface = surface;
      wave.style.transform = isCircle && surface === 'hover'
        ? 'translate3d(0,0,0)'
        : 'translate3d(0,var(--aoctant-button-wave-start-y),0)';
      wave.style.background = `var(--aoctant-button-wave-${surface}-background)`;
      foreground.sweep.style.clipPath = isCircle && surface === 'hover'
        ? 'ellipse(75% 100% at 50% 50%)'
        : 'ellipse(75% 100% at 50% 178%)';
      foreground.sweep.style.color = `var(--aoctant-button-wave-${surface}-foreground)`;
      if (checkoutLock) checkoutLock.style.color = `var(--aoctant-button-wave-${surface}-foreground)`;
    };

    const request = (surface) => {
      state.desired = surface;
      if (state.nativeOwned) return;

      if (state.running) {
        if (surface === state.activeTarget) return;

        state.generation += 1;
        cancelAnimations();
        commit(state.activeTarget);
        state.running = false;
      }

      if (state.committed === surface) return;

      const generation = ++state.generation;
      const startY = surface === 'rest'
        ? cssVar(control, '--aoctant-button-wave-exit-start-y', '74%')
        : cssVar(control, '--aoctant-button-wave-start-y', '78%');
      const easing = cssVar(control, '--aoctant-button-wave-ease', 'cubic-bezier(.22, .75, .2, 1)');
      state.running = true;
      state.activeTarget = surface;
      const isCircleExit = isCircle && surface === 'rest';

      if (reducedMotion.matches) {
        cancelAnimations();
        commit(surface);
        state.running = false;
        return;
      }

      const timing = { duration: duration(control), easing, fill: 'forwards' };

      if (isCircleExit) {
        /*
         * Circle exit:
         * Keep the real button shell white and move the existing BLACK
         * completed wave upward out of the circle.
         *
         * This reveals the native/rest white surface bottom-to-top without
         * placing a black hold surface beneath a white child layer.
         */
        const exitY = startY.startsWith('-') ? startY : `-${startY}`;

        wave.style.background = 'var(--aoctant-button-wave-hover-background)';
        foreground.sweep.style.color = 'var(--aoctant-button-wave-hover-foreground)';

        wave.style.transform = 'translate3d(0,0,0)';
        foreground.sweep.style.clipPath = 'ellipse(75% 100% at 50% 50%)';

        void wave.offsetWidth;

        state.waveAnimation = wave.animate(
          [
            { transform: 'translate3d(0,0,0)' },
            { transform: `translate3d(0,${exitY},0)` },
          ],
          timing
        );

        state.foregroundAnimation = foreground.sweep.animate(
          [
            { clipPath: 'ellipse(75% 100% at 50% 50%)' },
            { clipPath: 'ellipse(75% 100% at 50% -100%)' },
          ],
          timing
        );
      } else {
        wave.style.background = `var(--aoctant-button-wave-${surface}-background)`;
        foreground.sweep.style.color = `var(--aoctant-button-wave-${surface}-foreground)`;

        wave.style.transform = `translate3d(0,${startY},0)`;
        foreground.sweep.style.clipPath = 'ellipse(75% 100% at 50% 178%)';

        void wave.offsetWidth;

        state.waveAnimation = wave.animate(
          [
            { transform: `translate3d(0,${startY},0)` },
            { transform: 'translate3d(0,0,0)' },
          ],
          timing
        );

        state.foregroundAnimation = foreground.sweep.animate(
          [
            { clipPath: 'ellipse(75% 100% at 50% 178%)' },
            { clipPath: 'ellipse(75% 100% at 50% 50%)' },
          ],
          timing
        );
      }
      if (checkoutLock) {
        const startColor = `var(--aoctant-button-wave-${state.committed}-foreground)`;
        const targetColor = `var(--aoctant-button-wave-${surface}-foreground)`;
        state.lockAnimation = checkoutLock.animate(
          [
            { color: startColor, offset: 0 },
            { color: startColor, offset: .45 },
            { color: targetColor, offset: .55 },
            { color: targetColor, offset: 1 },
          ],
          timing
        );
      }

      state.waveAnimation.finished
        .then(() => {
          if (generation !== state.generation) return;
          commit(surface);
          cancelAnimations();
          state.running = false;
          state.activeTarget = surface;
        })
        .catch(() => {
          if (generation !== state.generation) return;
          state.waveAnimation = null;
          state.foregroundAnimation = null;
          state.lockAnimation = null;
          state.running = false;
        });
    };

    const syncNativeState = () => {
      const nativeState = getNativeState(control);
      if (nativeState) {
        state.nativeOwned = true;
        state.generation += 1;
        state.running = false;
        cancelAnimations();
        commit('rest');
        state.desired = 'rest';
        control.dataset.aoctantWaveNativeState = nativeState;
        if (nativeState === 'processing' && !hasNativeProcessingFeedback(control)) {
          control.dataset.aoctantWaveLoading = 'true';
        } else {
          control.removeAttribute('data-aoctant-wave-loading');
        }
        return;
      }
      if (!state.nativeOwned) return;

      state.nativeOwned = false;
      control.removeAttribute('data-aoctant-wave-native-state');
      control.removeAttribute('data-aoctant-wave-loading');
      state.desired = 'rest';
      commit('rest');
      request(finePointer.matches && control.matches(':hover') ? 'hover' : 'rest');
    };

    const onEnter = () => {
      if (finePointer.matches && !getNativeState(control)) request('hover');
    };
    const onLeave = () => {
      if (finePointer.matches && !getNativeState(control)) request('rest');
    };
    control.addEventListener('mouseenter', onEnter);
    control.addEventListener('mouseleave', onLeave);
    bindings.set(control, {
      intact: () => control.dataset.aoctantWaveBound === 'true' &&
        [wave, foreground.base, foreground.sweep, loadingFeedback].every((node) => node.parentNode === control),
      dispose: () => {
        state.generation += 1;
        cancelAnimations();
        control.removeEventListener('mouseenter', onEnter);
        control.removeEventListener('mouseleave', onLeave);
      },
    });
    control.aoctantWaveRequest = request;
    control.aoctantWaveSyncNativeState = syncNativeState;
    controls.add(control);
    syncNativeState();
  };

  const bindWithin = (root) => {
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    if (root.matches(targetSelector)) bind(root);
    root.querySelectorAll(targetSelector).forEach(bind);
  };
  bindWithin(root);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // Horizon can retain a control while morphing away its decoration children.
      const control = mutation.target.closest?.(targetSelector);
      if (control && root.contains(control)) bind(control);
      mutation.addedNodes.forEach(bindWithin);
      if (mutation.type === 'attributes') {
        mutation.target.closest?.(targetSelector)?.aoctantWaveSyncNativeState?.();
      }
    });
  });
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'data-added', 'class'],
  });

  const resynchronize = () => {
    controls.forEach((control) => {
      if (!control.isConnected) {
        controls.delete(control);
        return;
      }
      control.aoctantWaveSyncNativeState?.();
      if (!getNativeState(control)) {
        control.aoctantWaveRequest?.(finePointer.matches && control.matches(':hover') ? 'hover' : 'rest');
      }
    });
  };
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resynchronize();
  });
  window.addEventListener('blur', () => {
    controls.forEach((control) => {
      if (!getNativeState(control)) control.aoctantWaveRequest?.('rest');
    });
  });
    window.addEventListener('focus', resynchronize);
  };

  const initializeRoots = () => {
    const roots = document.querySelectorAll(sharedRootSelector);
    roots.forEach(initialize);
    return roots.length;
  };

  document.addEventListener('shopify:section:load', initializeRoots);

  if (initializeRoots()) return;

  const rootObserver = new MutationObserver(() => {
    if (!initializeRoots()) return;
    rootObserver.disconnect();
  });

  rootObserver.observe(document.documentElement || document, {
    childList: true,
    subtree: true,
  });
})();
